"use client";

import Image from "next/image";
import Link from "next/link";
import styles from "./sponsor-logos.module.scss";
import { Container } from "react-bootstrap";
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useMotionValue, useAnimationFrame, useReducedMotion } from "framer-motion";

type Sponsor = { src: string; text: string; url: string };

export default function SponsorLogos() {
  const shouldReduce = useReducedMotion();

  const sponsors = useMemo<Sponsor[]>(
    () => [
      { src: "/logos/ceadar-logo.png", text: "CeADAR Logo", url: "https://ceadar.ie/" },
      { src: "/logos/icrag-logo.png", text: "iCRAG Logo", url: "https://www.icrag-centre.org/" },
      { src: "/logos/research-ireland.png", text: "Taighde Éireann - Research Ireland", url: "https://www.researchireland.ie/" },
      { src: "/logos/npws.png", text: "NPWS Logo", url: "https://www.npws.ie/" },
      { src: "/logos/eu.png", text: "European Union", url: "https://next-generation-eu.europa.eu" },
      { src: "/logos/gov-ireland.png", text: "Government of Ireland", url: "https://www.gov.ie" },
      { src: "/logos/national-challenge-fund.png", text: "National Challenge Fund", url: "https://www.sfi.ie/challenges/National-Challenge-Fund/" },
    ],
    []
  );

  const [isClient, setIsClient] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsClient(true);
    const mq = window.matchMedia("(max-width: 576px)");
    const handler = (e: MediaQueryListEvent | MediaQueryList) =>
      setIsMobile("matches" in e ? e.matches : (e as MediaQueryList).matches);

    handler(mq);
    mq.addEventListener?.("change", handler as (e: MediaQueryListEvent) => void);
    mq.addListener?.(handler as any); 

    return () => {
      mq.removeEventListener?.("change", handler as (e: MediaQueryListEvent) => void);
      mq.removeListener?.(handler as any);
    };
  }, []);

  const [repeatCount, setRepeatCount] = useState(3);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const repeated = useMemo(
    () => Array.from({ length: repeatCount }).flatMap(() => sponsors),
    [repeatCount, sponsors]
  );

  const x = useMotionValue(0);
  const [cycleWidth, setCycleWidth] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!isClient || isMobile) return;

    const el = trackRef.current;
    const container = containerRef.current;
    if (!el || !container) return;

    const measure = () => {
      const total = el.scrollWidth;
      const single = repeatCount > 0 ? total / repeatCount : 0;
      const cw = container.clientWidth;

      if (single > 0) {
        const needed = Math.max(3, Math.ceil((cw * 2) / single));
        if (needed !== repeatCount) {
          setRepeatCount(needed);
          return;
        }
        setCycleWidth(single);
      }
    };

    measure();
    const raf = requestAnimationFrame(measure);

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    ro.observe(container);
    window.addEventListener("resize", measure);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      cancelAnimationFrame(raf);
    };
  }, [repeatCount, isClient, isMobile]);

  useAnimationFrame((_, delta) => {
    if (shouldReduce || paused || cycleWidth === 0 || isMobile || !isClient) return;

    const durationMs = 30000; 
    const pxPerMs = cycleWidth / durationMs;
    const next = x.get() - pxPerMs * delta;
    const wrapped = ((next + cycleWidth) % cycleWidth) - cycleWidth;
    x.set(wrapped);
  });

  // ---- Static grid for mobile or pre-hydration ----
  if (isMobile || !isClient) {
    return (
      <section className={styles.wrapper} aria-labelledby="partners-heading">
        <Container>
          <h3 id="partners-heading" className={styles.title}>Our Partners</h3>
          <div className={styles.staticSponsors} aria-live="off">
            {sponsors.map((s, idx) => (
              <div key={s.src} className={styles.marqueeItem}>
                <Link
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.card}
                  aria-label={`${s.text} — opens in a new tab`}
                >
                  <div className={styles.imageWrap}>
                    <Image
                      src={s.src}
                      alt={s.text}
                      fill
                      sizes="(max-width: 576px) 150px, 180px"
                      className={styles.img}
                      loading={idx < 4 ? "eager" : "lazy"}
                    />
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </Container>
        <div className={styles.backgroundElements}>
    <div className={styles.blueOrb1}></div>
    <div className={styles.blueOrb2}></div>
    <div className={styles.blueOrb3}></div>
  </div>
      </section>
    );
  }

  // ---- Animated marquee for Desktop screens ----
  return (
    <section className={styles.wrapper} aria-labelledby="partners-heading">
      <Container>
        <h3 id="partners-heading" className={styles.title}>Our Partners</h3>

        <div
          className={styles.marqueeContainer}
          ref={containerRef}
          tabIndex={0}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
        >
          <motion.div
            className={styles.marqueeTrack}
            ref={trackRef}
            style={{ x, willChange: "transform" }}
            aria-live="off"
          >
            {repeated.map((s, idx) => (
              <div key={`${idx}-${s.src}`} className={styles.marqueeItem}>
                <Link
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.card}
                  aria-label={`${s.text} — opens in a new tab`}
                >
                  <div className={styles.imageWrap}>
                    <Image
                      src={s.src}
                      alt={s.text}
                      fill
                      sizes="180px"
                      className={styles.img}
                      loading="eager"
                      priority={idx < sponsors.length}
                    />
                  </div>
                </Link>
              </div>
            ))}
          </motion.div>
        </div>
      </Container>
    </section>
  );
}